import { useState, useEffect } from 'react';
import { useOffboardingStore, type OffboardingRecord } from '../../../store/offboardingStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from '../../../components/Dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator
} from '../../../components/DropdownMenu';
import { 
  Plus, Search, UserMinus, FileSignature, Laptop, CheckCircle2, 
  MoreHorizontal, Trash2, Calendar, AlertTriangle, CheckSquare, Square,
  Building2, UserCheck, Mail
} from 'lucide-react';
import toast from 'react-hot-toast';
import { EmployeeHoldingsPanel } from '../../../components/assets/EmployeeAssets';

const heldOf = (r: OffboardingRecord) => (r.assetsHeld || 0) + (r.licensesHeld || 0);

const STATUS_FILTERS = [
  { id: 'all', label: 'All Records' },
  { id: 'initiated', label: 'In Progress' },
  { id: 'completed', label: 'Completed' },
];

const DEPARTURE_REASONS = [
  'Better Career Opportunity / Growth',
  'Higher Compensation & Benefits',
  'Relocation / Moving City',
  'Personal / Family Reasons',
  'Higher Studies / Education',
  'Health & Medical',
  'Involuntary / Involuntary Termination',
  'Retirement',
  'Contract Completion',
  'Other',
];

export function Offboarding() {
  const { records, isLoading, fetchRecords, createRecord, updateRecord, deleteRecord } = useOffboardingStore();
  const { employees, fetchEmployees } = useEmployeeStore();
  const [assetsFor, setAssetsFor] = useState<OffboardingRecord | null>(null);

  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newRecord, setNewRecord] = useState({
    employeeId: '',
    resignationDate: new Date().toISOString().slice(0, 10),
    lastWorkingDay: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    reason: 'Better Career Opportunity / Growth',
  });

  useEffect(() => {
    fetchRecords();
    fetchEmployees();
  }, [fetchRecords, fetchEmployees]);

  const filteredRecords = records.filter(r => {
    const q = search.toLowerCase().trim();
    const name = `${r.employeeFirstName || ''} ${r.employeeLastName || ''}`.toLowerCase();
    const email = (r.employeeEmail || '').toLowerCase();
    const reason = (r.reason || '').toLowerCase();
    const matchesSearch = !q || name.includes(q) || email.includes(q) || reason.includes(q);
    const matchesStatus = 
      selectedStatus === 'all' || 
      (selectedStatus === 'initiated' && r.status !== 'completed') ||
      r.status.toLowerCase() === selectedStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const openCreateModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    const thirtyDays = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

    const activeEmps = employees.filter(e => e.status === 'active');
    setNewRecord({
      employeeId: activeEmps.length > 0 ? activeEmps[0].id : '',
      resignationDate: today,
      lastWorkingDay: thirtyDays,
      reason: 'Better Career Opportunity / Growth',
    });
    setIsCreateModalOpen(true);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRecord.employeeId) {
      toast.error('Please select an employee');
      return;
    }

    if (new Date(newRecord.lastWorkingDay) < new Date(newRecord.resignationDate)) {
      toast.error('Last working day cannot be earlier than resignation date');
      return;
    }

    try {
      await createRecord({
        employeeId: newRecord.employeeId,
        resignationDate: new Date(newRecord.resignationDate).toISOString(),
        lastWorkingDay: new Date(newRecord.lastWorkingDay).toISOString(),
        reason: newRecord.reason,
        status: 'initiated',
      });
      toast.success('Offboarding workflow initiated successfully');
      setIsCreateModalOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to initiate offboarding');
    }
  };

  const handleToggleExitInterview = async (record: OffboardingRecord) => {
    const newVal = !record.exitInterviewCompleted;
    try {
      await updateRecord(record.id, { exitInterviewCompleted: newVal });
      toast.success(`Exit interview marked as ${newVal ? 'Completed' : 'Pending'}`);
    } catch (err) {
      toast.error('Failed to update exit interview status');
    }
  };

  const handleToggleAssets = async (record: OffboardingRecord) => {
    const newVal = !record.assetsRecovered;
    // Still holding something: open the Asset Hub view instead of a blind toggle.
    if (newVal && heldOf(record) > 0) { setAssetsFor(record); return; }
    try {
      await updateRecord(record.id, { assetsRecovered: newVal });
      toast.success(`IT Assets marked as ${newVal ? 'Recovered' : 'Pending'}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update assets status');
    }
  };

  const handleCompleteOffboarding = async (record: OffboardingRecord) => {
    if (heldOf(record) > 0) {
      toast.error('Recover the assets and licence seats this employee holds before completing offboarding.');
      setAssetsFor(record);
      return;
    }
    if (!record.exitInterviewCompleted || !record.assetsRecovered) {
      if (!window.confirm('Some clearances (exit interview or assets) are still pending. Do you wish to proceed and mark offboarding as completed?')) {
        return;
      }
    }

    try {
      await updateRecord(record.id, {
        status: 'completed',
        exitInterviewCompleted: true,
        assetsRecovered: true,
      });
      toast.success('Offboarding completed. Employee record updated to Resigned/Terminated.');
      fetchEmployees(); // Refresh employees store
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete offboarding');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to remove offboarding record for ${name}?`)) {
      try {
        await deleteRecord(id);
        toast.success('Offboarding record deleted');
      } catch (err) {
        toast.error('Failed to delete offboarding record');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">
            Offboarding & Exit Clearances
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage employee separations, exit interviews, IT asset recovery, and final clearances.
          </p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="size-4" /> Initiate offboarding
        </Button>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
              <UserMinus className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">In Progress</p>
              <h3 className="text-foreground font-display text-metric">
                {records.filter(r => r.status !== 'completed').length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <FileSignature className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Pending Exit Interview</p>
              <h3 className="text-foreground font-display text-metric">
                {records.filter(r => r.status !== 'completed' && !r.exitInterviewCompleted).length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-destructive/10 flex items-center justify-center text-destructive">
              <Laptop className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Pending Assets</p>
              <h3 className="text-foreground font-display text-metric">
                {records.filter(r => r.status !== 'completed' && (!r.assetsRecovered || heldOf(r) > 0)).length}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
              <CheckCircle2 className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Completed Clearances</p>
              <h3 className="text-foreground font-display text-metric">
                {records.filter(r => r.status === 'completed').length}
              </h3>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Card */}
      <Card className="shadow-xs border-border">
        <CardHeader className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap gap-1">
              {STATUS_FILTERS.map(f => (
                <Button
                  key={f.id}
                  variant={selectedStatus === f.id ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedStatus(f.id)}
                  className="text-xs h-8"
                >
                  {f.label}
                </Button>
              ))}
            </div>

            <div className="w-full sm:w-64">
              <Input
                leftIcon={<Search className="size-4 text-muted-foreground" />}
                placeholder="Search employee or reason…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Loading offboarding records…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-secondary/40 text-muted-foreground font-medium text-xs border-b border-border">
                  <tr>
                    <th className="px-6 py-3">Employee</th>
                    <th className="px-6 py-3">Resignation Date</th>
                    <th className="px-6 py-3">Last Working Day</th>
                    <th className="px-6 py-3">Exit Interview</th>
                    <th className="px-6 py-3">IT Assets</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredRecords.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                        No offboarding records found matching the filter.
                      </td>
                    </tr>
                  ) : (
                    filteredRecords.map((r) => (
                      <tr key={r.id} className="hover:bg-secondary/20 transition-colors">
                        <td className="px-6 py-3.5">
                          <div className="font-medium text-foreground">
                            {r.employeeFirstName || 'Employee'} {r.employeeLastName || ''}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                            <Mail className="size-3" /> {r.employeeEmail}
                          </div>
                        </td>
                        <td className="px-6 py-3.5 text-muted-foreground tabular-nums text-sm">
                          {r.resignationDate ? new Date(r.resignationDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                        <td className="px-6 py-3.5 text-foreground font-medium tabular-nums text-sm">
                          {r.lastWorkingDay ? new Date(r.lastWorkingDay).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                        <td className="px-6 py-3.5">
                          <button
                            type="button"
                            onClick={() => handleToggleExitInterview(r)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            {r.exitInterviewCompleted ? (
                              <Badge variant="success" dot>Completed</Badge>
                            ) : (
                              <Badge variant="warning" dot>Pending</Badge>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-3.5">
                          <button
                            type="button"
                            onClick={() => (heldOf(r) > 0 ? setAssetsFor(r) : handleToggleAssets(r))}
                            className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
                            title={heldOf(r) > 0 ? 'View and recover in the Asset Hub' : undefined}
                          >
                            {heldOf(r) > 0 ? (
                              <Badge variant="destructive" dot>
                                {[r.assetsHeld ? `${r.assetsHeld} asset${r.assetsHeld > 1 ? 's' : ''}` : '', r.licensesHeld ? `${r.licensesHeld} seat${r.licensesHeld > 1 ? 's' : ''}` : ''].filter(Boolean).join(' · ')} held
                              </Badge>
                            ) : r.assetsRecovered ? (
                              <Badge variant="success" dot>Recovered</Badge>
                            ) : (
                              <Badge variant="destructive" dot>Pending</Badge>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-3.5">
                          {r.status === 'completed' ? (
                            <Badge variant="success">Completed</Badge>
                          ) : (
                            <Badge variant="primary" dot>In Progress</Badge>
                          )}
                          {r.reason && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[160px]" title={r.reason}>
                              {r.reason}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {r.status !== 'completed' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCompleteOffboarding(r)}
                                className="text-xs h-7 gap-1"
                              >
                                <CheckCircle2 className="size-3.5 text-success" /> Finalize Exit
                              </Button>
                            )}

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon-sm">
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleToggleExitInterview(r)}>
                                  <FileSignature className="size-4 mr-2" />
                                  {r.exitInterviewCompleted ? 'Mark Interview as Pending' : 'Mark Interview as Completed'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setAssetsFor(r)}>
                                  <Laptop className="size-4 mr-2" /> View IT assets
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleAssets(r)}>
                                  <Laptop className="size-4 mr-2" />
                                  {r.assetsRecovered ? 'Mark Assets as Pending' : 'Mark Assets as Recovered'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem destructive onClick={() => handleDelete(r.id, `${r.employeeFirstName} ${r.employeeLastName}`)}>
                                  <Trash2 className="size-4 mr-2" /> Delete Record
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Initiate Offboarding Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Initiate Employee Offboarding</DialogTitle>
            <DialogDescription>
              Record employee departure details, calculate notice period, and trigger exit checklists.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateSubmit} className="space-y-4 pt-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Select Employee *</label>
              <select
                required
                className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                value={newRecord.employeeId}
                onChange={(e) => setNewRecord({ ...newRecord, employeeId: e.target.value })}
              >
                <option value="">Select an employee...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName} — {emp.designation || 'Team Member'} ({emp.department || 'General'})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Resignation Date *</label>
                <Input
                  required
                  type="date"
                  value={newRecord.resignationDate}
                  onChange={(e) => setNewRecord({ ...newRecord, resignationDate: e.target.value })}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Last Working Day (LWD) *</label>
                <Input
                  required
                  type="date"
                  value={newRecord.lastWorkingDay}
                  onChange={(e) => setNewRecord({ ...newRecord, lastWorkingDay: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Primary Reason for Leaving</label>
              <select
                className="w-full bg-field border border-input rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
                value={newRecord.reason}
                onChange={(e) => setNewRecord({ ...newRecord, reason: e.target.value })}
              >
                {DEPARTURE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Initiate Offboarding
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!assetsFor} onOpenChange={(o) => { if (!o) { setAssetsFor(null); fetchRecords(); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>IT assets — {assetsFor?.employeeFirstName} {assetsFor?.employeeLastName}</DialogTitle>
            <DialogDescription>Live from the Asset Hub. Recover everything before completing offboarding.</DialogDescription>
          </DialogHeader>
          {assetsFor && <EmployeeHoldingsPanel employeeId={assetsFor.employeeId} allowAssign={false} />}
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setAssetsFor(null); fetchRecords(); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
